import type { Signal } from '@preact/signals-react';
import { isEqual } from 'lodash';
import type { Ref } from 'react';
import { forwardRef, useEffect } from 'react';
import useStateRef from '../../../common/hooks/use-state-ref';
import type { TypeOrArray } from '../../common-interfaces';
import type { ContinuousScrollerFilter } from '../../continuous-scroller/continuous-scroller-types';
import type { DynamicFieldComponentProps, DynamicFieldDataProps } from '../../dynamic-form/dynamic-form.interfaces';
import { DataSearchLocation } from '../../enums';
import type { OdinIconSize } from '../../odin-icon/odin-icon.component';
import type { ActionSectionItem } from '../drop-down/drop-down-action-section.component';
import type { DropDownFeatures } from '../drop-down/drop-down-features';
import type { DropDownNoSelection, DropDownResult } from '../drop-down/drop-down-types';
import { DropDownSelectionType } from '../drop-down/drop-down-types';
import type { DropDownRef } from '../drop-down/drop-down.component';
import DropDown from '../drop-down/drop-down.component';
import { useTranslation } from 'react-i18next';

export interface DropDownMultiSelectProps
  extends DynamicFieldComponentProps<TypeOrArray<DropDownResult> | DropDownNoSelection>, DynamicFieldDataProps {
  allowClear?: boolean;
  allowSearch?: boolean;
  showArchivedOptions?: boolean;
  /**
   * If true this will cause the data to load when the component is created instead of when the drop-down-old list is
   * opened (default: false).
   */
  preloadData?: boolean;
  multipleMessageCount?: number;
  multipleMessageText?: string;
  useChips?: boolean;
  filterSignal?: Signal<Array<ContinuousScrollerFilter>>;
  filterLocation?: DataSearchLocation;
  actionItems?: Array<ActionSectionItem>;
  onActionItemChanged?: (item: ActionSectionItem, features: DropDownFeatures) => void;
  className?: string;
  containerStyles?: string;
  labelStyles?: string;
  dropDownStyles?: string;
  dropDownIconSize?: OdinIconSize;
  dropDownButtonStyles?: string;
  clearButtonStyles?: string;
  onDoubleClick?: (value: DropDownResult) => void;
  shouldUseCreatePortal?: boolean;
  allowDuplicateCall?: boolean;
  onSelectorVisible?: (visible: boolean) => void;
}

const DropDownMultiSelect = (props: DropDownMultiSelectProps, ref: Ref<DropDownRef>) => {
  const {
    id,
    textField,
    valueField,
    archivedField,
    label,
    value,
    data,
    error,
    allowClear = false,
    allowSearch = true,
    showArchivedOptions = false,
    preloadData = false,
    multipleMessageCount = 0,
    multipleMessageText,
    useChips = true,
    placeholder,
    filterSignal,
    filterLocation = DataSearchLocation.Local,
    actionItems,
    onActionItemChanged,
    readOnly = false,
    required,
    onChange,
    inputRef,
    className,
    containerStyles,
    labelStyles,
    dropDownStyles,
    dropDownIconSize,
    dropDownButtonStyles,
    clearButtonStyles,
    onDoubleClick,
    disabled = false,
    shouldUseCreatePortal,
    allowDuplicateCall,
    pageSize = 50,
    onSelectorVisible,
  } = props;

  const [valueState, setValueState, valueRef] = useStateRef<Array<DropDownResult>>();
  const { t } = useTranslation();

  useEffect(() => {
    if (Array.isArray(value) && !isEqual(value, valueRef.current)) {
      setValueState(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- setValueState and valueRef are stable from useStateRef hook
  }, [value]);

  return (
    <DropDown
      ref={ref}
      id={id}
      textField={textField}
      valueField={valueField}
      archivedField={archivedField}
      label={label}
      value={valueState}
      data={data}
      selectionType={DropDownSelectionType.Multiple}
      error={error}
      allowClear={allowClear}
      allowSearch={allowSearch}
      showArchivedOptions={showArchivedOptions}
      preloadData={preloadData}
      placeholder={placeholder}
      filterSignal={filterSignal}
      filterLocation={filterLocation}
      actionItems={actionItems}
      onActionItemChanged={onActionItemChanged}
      readOnly={readOnly}
      required={required}
      useChips={useChips}
      multipleMessageCount={multipleMessageCount}
      multipleMessageText={multipleMessageText ?? t('multiple', 'Multiple')}
      inputRef={inputRef}
      className={className}
      containerStyles={containerStyles}
      labelStyles={labelStyles}
      dropDownStyles={dropDownStyles}
      dropDownIconSize={dropDownIconSize}
      dropDownButtonStyles={dropDownButtonStyles}
      clearButtonStyles={clearButtonStyles}
      onChange={onChange}
      onDoubleClick={onDoubleClick}
      setDefaultValue={false}
      disabled={disabled}
      shouldUseCreatePortal={shouldUseCreatePortal}
      allowDuplicateCall={allowDuplicateCall}
      pageSize={pageSize}
      onSelectorVisible={onSelectorVisible}
    />
  );
};

export default forwardRef(DropDownMultiSelect);
